import { IndexedDBBlocks } from './indexeddb-blocks';
import { GBFDecoder } from './gbf/gbf';
import { SessionDataManagerOptions } from './types';
import { BLOCK_MS, MANIFEST_STORE, SESSION_DB_NAME, SESSION_DB_VERSION, SLOT_DURATION, STORE_NAME } from './constants';
import { type GbfValidation, type SessionManifest, validateGbfBuffer } from './manifest';
import { getSlotId } from './utils';

const DEBUG = false;
const logger = (...content) => DEBUG && console.log(content);

/**
 * Manages session-related data using IndexedDB as the storage backend.
 * Provides methods to store, retrieve, and delete data blocks as well as utilities
 * for working with timestamp-aligned blocks of data.
 */
export class SessionDataManager {
  public db: any;

  // Cache per decoder, vengono liberati automaticamente quando l'oggetto athSession viene GC
  private decoderCache = new WeakMap<any, GBFDecoder>();
  private cacheSize = 0;
  private maxCacheSize = 50; // Limite cache

  /**
   * options: { dbName, version }
   */
  constructor(options: SessionDataManagerOptions = {}) {
    this.db = new IndexedDBBlocks({
      name: options.dbName || SESSION_DB_NAME,
      stateStore: STORE_NAME,
      version: options.version || SESSION_DB_VERSION,
      extraStores: [{ name: MANIFEST_STORE, keyPath: 'sessionId' }],
    });
  }

  // Align timestamp to block start
  static blockStartFor(timestamp: number) {
    return Math.floor(timestamp / BLOCK_MS) * BLOCK_MS;
  }

  /**
   * Opens a connection to the database.
   *
   * @return A promise that resolves when the database connection has been successfully opened.
   */
  async open() {
    return this.db.open();
  }

  /**
   * Saves the given block data for a specific session and athlete in the database.
   *
   * @param sessionId The unique identifier of the session.
   * @param athleteId The unique identifier of the athlete.
   * @param block The block data to be saved, defaults to an empty array.
   * @return A boolean indicating whether the block was successfully saved.
   */
  async saveBlock(sessionId: number, athleteId: number, block = []) {
    if (!block) false;

    await this.open();

    await this.db.set([sessionId, athleteId], block);
    return true;
  }

  /**
   * Loads and retrieves blocks of session data for a specific athlete based on the provided session and athlete IDs.
   *
   * @param sessionId The ID of the session to be queried.
   * @param athleteId The ID of the athlete to be queried.
   * @return An object containing the sorted data rows associated with the provided session and athlete IDs.
   */
  async loadBlock(sessionId: number, athleteId: number) {
    // @todo si potrebbe aggiungere un attributo per salvare l'ultimo accesso da valutare durante il GC

    await this.open();
    // Build IDBKeyRange.only on the compound index
    const key = [sessionId, athleteId];
    const range = IDBKeyRange.only(key);
    // Use queryByIndex (which tries getAll on index)
    const rows = await this.db.get(STORE_NAME, 'session-data-state', range);
    // sort by timestamp just in case
    rows.sort((a, b) => a.time - b.time);
    return { rows };
  }

  async isDataBlockAvailable(sessionId: number) {
    await this.open();
    const range = IDBKeyRange.bound([sessionId], [sessionId,[]]);

    return await this.db.exists(range);
  }

  // ---- manifest + completeness helpers -------------------------------------------------

  async getManifest(sessionId: string): Promise<SessionManifest | null> {
    await this.open();
    return (await this.db.getFrom(MANIFEST_STORE, String(sessionId))) || null;
  }

  async saveManifest(manifest: SessionManifest) {
    await this.open();
    return this.db.putTo(MANIFEST_STORE, { ...manifest, updatedAt: Date.now() });
  }

  /** athlete ids that have a stored block for the session (keys only, no data read) */
  async getStoredAthleteIds(sessionId: string): Promise<string[]> {
    await this.open();
    const keys = await this.db.getAllKeys(IDBKeyRange.bound([sessionId], [sessionId, []]));
    return (keys as any[]).map((k) => String(k[1]));
  }

  /** validates the stored blocks of the given athletes (blocks saved before the manifest existed) */
  async validateStoredBlocks(sessionId: string, athleteIds: string[]) {
    const validations: Record<string, GbfValidation> = {};
    const bytes: Record<string, number> = {};

    await this.open();
    for (const athleteId of athleteIds) {
      const row = await this.db.get([sessionId, athleteId]);
      const data = row?.data;
      bytes[athleteId] = data?.byteLength ?? 0;
      validations[athleteId] = data && typeof data.byteLength === 'number'
        ? validateGbfBuffer(data)
        : { ok: false, records: 0, firstTime: null, lastTime: null, error: 'missing or invalid block' };
    }

    return { validations, bytes };
  }

  async deleteStoredBlock(sessionId: string, athleteId: string) {
    await this.open();
    return this.db.deleteKey([sessionId, athleteId]);
  }

  /** drops every block and the manifest of a session */
  async deleteSession(sessionId: string) {
    await this.open();
    await this.db.deleteKey(IDBKeyRange.bound([sessionId], [sessionId, []]));
    await this.db.deleteFrom(MANIFEST_STORE, String(sessionId));
    return true;
  }

  /**
   * Cleanup della cache quando raggiunge il limite
   */
  private cleanupCache() {
    if (this.cacheSize > this.maxCacheSize) {
      // Reset della cache per liberare memoria
      this.decoderCache = new WeakMap<any, GBFDecoder>();
      this.cacheSize = 0;

      // Forza garbage collection se disponibile
      if (global.gc) {
        global.gc();
      }
    }
  }

  /**
   * Loads a block of session data for a specified time range.
   *
   * @param sessionId Identifier of the session to retrieve data for.
   * @param timestampStart Start time (inclusive) of the data block to be loaded.
   * @return Object containing session data organized by athlete IDs. Each athlete's data is mapped to a set of timestamps and corresponding data points.
   */
  async loadDataBlock(sessionId: number, timestampStart: number) {
    await this.open();
    const range = IDBKeyRange.bound([sessionId], [sessionId, []]);
    const athSessions = await this.db.getAll(range);

    const output: {[k: number]: any} = {};

    for (let i = 0; i < athSessions.length; i++) {
      const athSession = athSessions[i];

      // Usa la cache WeakMap per i decoder
      let decoder = this.decoderCache.get(athSession);
      if (!decoder) {
        decoder = new GBFDecoder(athSession.data);

        this.decoderCache.set(athSession, decoder);
        this.cacheSize++;
      }

      let records;
      try {
        ({ records } = decoder.decode());
      } catch (e) {
        // corrupt block: skip this athlete instead of failing the whole slot
        console.error(`Blocco non decodificabile per ${sessionId} - ${athSession.athleteId}`, e);
        continue;
      }

      const tStart = timestampStart;
      const tEnd = getSlotId(timestampStart) * SLOT_DURATION + SLOT_DURATION - 1;

      // Pre-filtra e processa in un solo passaggio con gestione memoria ottimizzata
      const filteredOutput = {};
      const recordsLength = records.length;

      for (let j = 0; j < recordsLength; j++) {
        const r = records[j];

        // Verifica che il record esista
        if (!r) continue;

        const timeMs = r.time * 1000;

        if (timeMs >= tStart && timeMs < tEnd) {
          filteredOutput[timeMs] = {
            x: r.x || r.latitude,
            y: r.y || r.longitude,
            cardio: r.cardio,
            heading: r.heading,
            speed: r.speed,
          };
        }

        // Libera immediatamente il riferimento per ridurre la memoria
        records[j] = null;

        // Cleanup periodico
        if (j % 5000 === 0 && global.gc) {
          global.gc();
        }
      }

      output[athSession.athleteId] = filteredOutput;
      // Libera i records dell'array
      records.length = 0;

      // Nullifica il riferimento ai dati dell'athSession
      athSession.data = null;
    }

    // Cleanup finale dell'array
    athSessions.length = 0;

    // Cleanup della cache se necessario
    this.cleanupCache();

    return output;
  }

  /**
   * Metodo per forzare la pulizia della cache
   */
  public clearCache(): void {
    this.decoderCache = new WeakMap<any, GBFDecoder>();
    this.cacheSize = 0;

    if (global.gc) {
      global.gc();
    }
  }

  /**
   * Deletes a block of data associated with the specified session ID and athlete ID
   * from the indexed database.
   *
   * @param sessionId The session identifier of the block to be deleted.
   * @param athleteId The athlete identifier of the block to be deleted.
   * @return A promise that resolves to true if the operation succeeds or rejects with an error if the operation fails.
   */
  async deleteBlock(sessionId: string, athleteId: string) {
    await this.open();
    const key = [sessionId, athleteId];
    const db = await this.db.open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_NAME], 'readwrite');
      const index = tx.objectStore(STORE_NAME).index('session-data-state');
      const range = IDBKeyRange.only(key);
      const cursorReq = index.openCursor(range);
      cursorReq.onsuccess = (ev) => {
        const cur = ev.target.result;
        if (!cur) return resolve(true);
        cur.delete();
        cur.continue();
      };
      cursorReq.onerror = (e) => reject(e.target.error);
    });
  }

  /**
   * Deletes all records associated with the specified session ID and athlete ID.
   *
   * @param sessionId The ID of the session to delete records for.
   * @param athleteId The ID of the athlete to delete records for.
   * @return A promise that resolves to true when all matching records are deleted, or rejects with an error.
   */
  async deleteAllFor(sessionId: string, athleteId: string) {
    await this.open();
    const db = await this.db.open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_NAME], 'readwrite');
      const index = tx.objectStore(STORE_NAME).index('session-data-state');
      const range = IDBKeyRange.bound([sessionId, athleteId, -Infinity], [sessionId, athleteId, +Infinity]);
      const cursorReq = index.openCursor(range);
      cursorReq.onsuccess = (ev) => {
        const cur = ev.target.result;
        if (!cur) return resolve(true);
        cur.delete();
        cur.continue();
      };
      cursorReq.onerror = (e) => reject(e.target.error);
    });
  }
}
